import { useEffect, useState } from 'react'

export const routes = ['/', '/features', '/infrastructure', '/security'] as const
export type Route = (typeof routes)[number]

const normalize = (pathname: string): Route => {
  const clean = (pathname.replace(/\/+$/, '') || '/') as Route
  return routes.includes(clean) ? clean : '/'
}

const scrollToHash = (hash: string) => {
  if (!hash) {
    window.scrollTo({ top: 0, behavior: 'auto' })
    return
  }
  // Wait a frame so the incoming route has committed before measuring.
  requestAnimationFrame(() => {
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

/**
 * Minimal history router. Intercepts same-origin anchor clicks so route changes
 * do not reload the document and section animations can replay.
 */
export function useRouter() {
  const [path, setPath] = useState<Route>(() => normalize(window.location.pathname))

  useEffect(() => {
    const onPopState = () => {
      setPath(normalize(window.location.pathname))
      scrollToHash(window.location.hash)
    }

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = (event.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return

      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return

      const next = normalize(url.pathname)
      event.preventDefault()

      if (next === path && !url.hash) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      window.history.pushState({}, '', url.pathname + url.hash)
      setPath(next)
      scrollToHash(url.hash)
    }

    window.addEventListener('popstate', onPopState)
    document.addEventListener('click', onClick)
    return () => {
      window.removeEventListener('popstate', onPopState)
      document.removeEventListener('click', onClick)
    }
  }, [path])

  return path
}
